// src/pages/ELearning/LearningPathConstructor.tsx — конструктор программы
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getElearningPathById, createElearningPath, updateElearningPath,
  getTests,
} from '../../api/client';
import {
  Loader2, Save, ArrowLeft, Route, Plus, Trash2,
  GripVertical, ChevronUp, ChevronDown, AlertCircle
} from 'lucide-react';

interface PathStep {
  testId: string;
  testTitle?: string;
  order: number;
  unlockAfterDays: number;
  deadlineDays: number;
  notes?: string;
}

const LearningPathConstructor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    description: '',
    mandatory: false,
    isOnboarding: false,
    active: true,
    steps: [] as PathStep[],
  });
  const [tests, setTests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const testsData = await getTests({ active: true });
        setTests(Array.isArray(testsData) ? testsData : (testsData.items || []));
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const d = await getElearningPathById(id);
        const p = d.path;
        setForm({
          name: p.name || '',
          description: p.description || '',
          mandatory: !!p.mandatory,
          isOnboarding: !!p.isOnboarding,
          active: p.active !== false,
          steps: (p.steps || []).map((s: any, i: number) => ({
            testId: String(s.testId?._id || s.testId),
            testTitle: s.testId?.title,
            order: s.order != null ? s.order : i,
            unlockAfterDays: s.unlockAfterDays || 0,
            deadlineDays: s.deadlineDays || 30,
            notes: s.notes || '',
          })),
        });
      } catch (e: any) {
        setError(e.response?.data?.error || 'Не удалось загрузить программу');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const addStep = () => {
    setForm({
      ...form,
      steps: [...form.steps, {
        testId: '',
        order: form.steps.length,
        unlockAfterDays: 0,
        deadlineDays: 30,
      }],
    });
  };

  const updateStep = (idx: number, patch: Partial<PathStep>) => {
    const next = [...form.steps];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, steps: next });
  };

  const removeStep = (idx: number) => {
    const next = form.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i }));
    setForm({ ...form, steps: next });
  };

  const moveStep = (idx: number, dir: -1 | 1) => {
    const next = [...form.steps];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((s, i) => { s.order = i; });
    setForm({ ...form, steps: next });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Введите название'); return; }
    if (form.steps.length === 0) { setError('Добавьте хотя бы один шаг'); return; }
    if (form.steps.some(s => !s.testId)) { setError('Выберите тест для каждого шага'); return; }

    setSaving(true);
    setError('');
    try {
      if (id) {
        await updateElearningPath(id, form);
      } else {
        await createElearningPath(form);
      }
      navigate('/app/elearning/paths');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Loader2 size={40} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .lpc-hero { animation: fadeUp 0.5s ease-out; }
        .lpc-section { animation: fadeUp 0.5s ease-out backwards; animation-delay: 0.1s; }
        .lpc-input:focus { border-color: transparent !important; box-shadow: 0 0 0 2px #667eea !important; }
        .lpc-btn { transition: all 0.3s ease; }
        .lpc-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102,126,234,0.4) !important; }
        .lpc-step { transition: all 0.25s ease; }
        .lpc-step:hover { box-shadow: 0 12px 40px rgba(31, 38, 135, 0.12) !important; }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <button onClick={() => navigate('/app/elearning/paths')} style={styles.backBtn}>
          <ArrowLeft size={16} /> К программам
        </button>
      </div>

      <div className="lpc-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <Route size={28} color="#fff" />
        </div>
        <div>
          <h1 style={styles.title}>
            {id ? 'Редактирование программы' : 'Новая программа'}
          </h1>
          <p style={styles.subtitle}>
            {id ? 'Обновите шаги и параметры' : 'Создайте последовательность тестов'}
          </p>
        </div>
      </div>

      {error && (
        <div style={styles.errorCard}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Секция 1 — Основное */}
      <div className="lpc-section" style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionBadge}>1</span>
          <h2 style={styles.sectionTitle}>Основное</h2>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Название *</label>
          <input
            type="text"
            className="lpc-input"
            placeholder="Например: Онбординг нового сотрудника"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Описание</label>
          <textarea
            className="lpc-input"
            placeholder="Краткое описание программы"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={styles.textarea}
          />
        </div>

        <div style={styles.togglesRow}>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={form.mandatory}
              onChange={e => setForm({ ...form, mandatory: e.target.checked })}
              style={{ width: 16, height: 16, accentColor: '#667eea' }}
            />
            <span>🔴 Обязательная программа</span>
          </label>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={form.isOnboarding}
              onChange={e => setForm({ ...form, isOnboarding: e.target.checked })}
              style={{ width: 16, height: 16, accentColor: '#667eea' }}
            />
            <span>✨ Онбординг (для новых)</span>
          </label>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => setForm({ ...form, active: e.target.checked })}
              style={{ width: 16, height: 16, accentColor: '#667eea' }}
            />
            <span>✅ Активна</span>
          </label>
        </div>
      </div>

      {/* Секция 2 — Шаги */}
      <div className="lpc-section" style={{ ...styles.section, animationDelay: '0.15s' }}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionBadge}>2</span>
          <h2 style={styles.sectionTitle}>Шаги программы</h2>
          <span style={styles.counter}>{form.steps.length}</span>
        </div>

        {form.steps.map((step, idx) => (
          <div key={idx} className="lpc-step" style={styles.stepCard}>
            <div style={styles.stepHeader}>
              <div style={styles.stepNum}>Шаг {idx + 1}</div>
              <div style={styles.stepActions}>
                <button
                  onClick={() => moveStep(idx, -1)}
                  disabled={idx === 0}
                  style={{ ...styles.iconBtn, opacity: idx === 0 ? 0.4 : 1 }}
                  title="Вверх"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => moveStep(idx, 1)}
                  disabled={idx === form.steps.length - 1}
                  style={{ ...styles.iconBtn, opacity: idx === form.steps.length - 1 ? 0.4 : 1 }}
                  title="Вниз"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  onClick={() => removeStep(idx)}
                  style={{ ...styles.iconBtn, color: '#ef4444' }}
                  title="Удалить"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Тест *</label>
              <select
                className="lpc-input"
                value={step.testId}
                onChange={e => updateStep(idx, { testId: e.target.value })}
                style={styles.input}
              >
                <option value="">— выберите тест —</option>
                {tests.map(t => (
                  <option key={t._id} value={t._id}>{t.title}</option>
                ))}
              </select>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Разблокировка через (дней)</label>
                <input
                  type="number"
                  className="lpc-input"
                  value={step.unlockAfterDays}
                  onChange={e => updateStep(idx, { unlockAfterDays: +e.target.value })}
                  style={styles.input}
                  min={0}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Дедлайн (дней от старта)</label>
                <input
                  type="number"
                  className="lpc-input"
                  value={step.deadlineDays}
                  onChange={e => updateStep(idx, { deadlineDays: +e.target.value })}
                  style={styles.input}
                  min={1}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Заметка</label>
              <input
                type="text"
                className="lpc-input"
                placeholder="Необязательно"
                value={step.notes || ''}
                onChange={e => updateStep(idx, { notes: e.target.value })}
                style={styles.input}
              />
            </div>
          </div>
        ))}

        <button onClick={addStep} style={styles.addStepBtn}>
          <Plus size={16} /> Добавить шаг
        </button>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="lpc-btn"
          style={styles.saveBtn}
        >
          {saving
            ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Сохранение...</>
            : <><Save size={16} /> {id ? 'Обновить' : 'Создать программу'}</>}
        </button>
        <button
          onClick={() => navigate('/app/elearning/paths')}
          style={styles.cancelBtn}
        >
          Отмена
        </button>
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'rgba(255,255,255,0.7)', color: '#5a5f7a', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', fontWeight: 700, fontSize: 13, cursor: 'pointer' },

  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(102,126,234,0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },

  errorCard: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 14, marginBottom: 16, fontWeight: 600, fontSize: 13 },

  section: { background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(24px) saturate(180%)', border: '1px solid rgba(255, 255, 255, 0.7)', borderRadius: 24, padding: 24, marginBottom: 20, boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  sectionBadge: { width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, boxShadow: '0 4px 12px rgba(102,126,234,0.3)' },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' },
  counter: { marginLeft: 'auto', padding: '4px 12px', background: 'rgba(102,126,234,0.12)', color: '#667eea', borderRadius: 12, fontSize: 13, fontWeight: 700 },

  formGroup: { marginBottom: 16 },
  label: { display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#5a5f7a' },
  input: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box', fontFamily: 'inherit' },
  textarea: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, minHeight: 80, outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },

  togglesRow: { display: 'flex', gap: 20, flexWrap: 'wrap', paddingTop: 8 },
  toggleLabel: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#5a5f7a', cursor: 'pointer' },

  stepCard: { background: 'rgba(255,255,255,0.7)', borderRadius: 18, padding: 20, marginBottom: 14, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 4px 16px rgba(31,38,135,0.05)' },
  stepHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  stepNum: { fontSize: 13, fontWeight: 800, color: '#667eea', padding: '4px 12px', background: 'rgba(102,126,234,0.1)', borderRadius: 10 },
  stepActions: { display: 'flex', gap: 4 },
  iconBtn: { padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#667eea', borderRadius: 8 },

  addStepBtn: { width: '100%', padding: '14px 20px', background: 'transparent', border: '2px dashed rgba(102,126,234,0.3)', color: '#667eea', borderRadius: 16, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 },

  actions: { display: 'flex', gap: 12, marginTop: 20, marginBottom: 40 },
  saveBtn: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '14px 28px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, boxShadow: '0 8px 24px rgba(102,126,234,0.35)' },
  cancelBtn: { background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 600, fontSize: 15, color: '#5a5f7a' },
};

export default LearningPathConstructor;
